import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useToast } from './Toast';
import '../styles/Modal.css';

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error('useModal must be used within ModalProvider');
  return context;
};

const Modal = ({ modal, onClose, isTopmost }) => {
  const { showToast } = useToast();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isTopmost) return;
      if (e.key === 'Escape') onClose(modal.id);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [modal.id, onClose, isTopmost]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && modal.closeOnBackdrop !== false) onClose(modal.id);
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div
        id={`modal-${modal.id}`}
        className={`modal-content ${modal.size ? `modal-${modal.size}` : 'modal-md'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {modal.showHeader !== false && (
          <div className="modal-header">
            <h3 className="modal-title">{modal.title || 'Modal'}</h3>
            {modal.showCloseButton !== false && (
              <button className="modal-close-btn" onClick={() => onClose(modal.id)}>
                <FaTimes />
              </button>
            )}
          </div>
        )}
        <div className="modal-body">
          {React.isValidElement(modal.component)
            ? React.cloneElement(modal.component, { closeModal: () => onClose(modal.id), showToast })
            : modal.component}
        </div>
      </div>
    </div>
  );
};

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]);

  const showModal = useCallback((options) => {
    const modal = {
      id: Date.now() + Math.random(),
      title: options.title,
      component: options.component,
      size: options.size || 'md',
      closeOnBackdrop: options.closeOnBackdrop !== false,
      showCloseButton: options.showCloseButton !== false,
      showHeader: options.showHeader !== false,
      onClose: options.onClose,
    };
    setModals(prev => [...prev, modal]);
    return modal.id;
  }, []);

  const closeModal = useCallback((id) => {
    setModals(prev => {
      const modal = prev.find(m => m.id === id);
      if (modal?.onClose && modal.onClose() === false) return prev;
      return prev.filter(m => m.id !== id);
    });
  }, []);

  const openModal = useCallback((title, component, size = 'md', options = {}) => {
    return showModal({ title, component, size, ...options });
  }, [showModal]);

  const closeAllModals = useCallback(() => setModals([]), []);

  return (
    <ModalContext.Provider value={{ showModal, openModal, closeModal, closeAllModals, modals }}>
      {children}
      {modals.map((modal, index) => (
        <Modal
          key={modal.id}
          modal={modal}
          onClose={closeModal}
          isTopmost={index === modals.length - 1}
        />
      ))}
    </ModalContext.Provider>
  );
};
